import { useEffect, useRef, useState, useContext } from "react";
import MainBtn from "../../main/main_btn/MainBtn";
import "./style.scss";
import { DispatchProps, StateProps } from "../../../store";
import { mapDispatchToProps, mapStateToProps } from "../../../store/dispatcher";
import { connect } from "react-redux";
import { Device, isTriPhaseDevice } from "../../../lib/Device";
import {
  circulatorPowerPercentMax,
  filterConfigsForModel,
  formatConfigOptionLabel,
  getCommissioningTipForLanguage,
  mergeCurrentConfig,
} from "../../../lib/parameterConfiguration";
import { App, Button, Input, Modal, Popconfirm, Select, Switch, Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import axios from "../../../services/axios";
import {
  InfoCircleOutlined,
  QuestionCircleOutlined,
  ReloadOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { ReactComponent as NewHome } from "../../../assets/icons/new-home.svg";
import { ReactComponent as NewDevice } from "../../../assets/icons/new-device.svg";
import CreateHomeModal from "../../home/create_home_modal/CreateHomeModal";
import CreateDeviceModal from "../../home/create_device_modal/CreateDeviceModal";
import ConfigureWiFiModal from "../../device/ConfigureWiFiModal";
import { CommissioningTipModal } from "../../device/ParameterConfigurationPicker";
import { hasCommissioningTip } from "../../../lib/sanitizeHtml";
import "../../device/parameterConfigurationSelect.scss";
import AuthContext from "../../../contexts/AuthContext";

const VALVE_POSITION_MAX_AGE_SECONDS = 300;
const VALVE_TELEMETRY_POLL_INTERVAL_MS = 120 * 1000;

type ValveTelemetrySnapshot = {
  position: string | null;
  updatedAt: number | null;
  fresh: boolean;
};

const isValveTelemetryFreshAt = (updatedAt: number | null) => {
  if (updatedAt == null) return false;
  return Date.now() / 1000 - updatedAt <= VALVE_POSITION_MAX_AGE_SECONDS;
};

const getValveTelemetryAgeMinutes = (updatedAt: number | null) => {
  if (updatedAt == null) return null;
  return Math.max(0, Math.floor((Date.now() / 1000 - updatedAt) / 60));
};

type ParameterConfig = {
  id: number;
  name: string;
  commissioning_tip_en?: string;
  commissioning_tip_fr?: string;
  commissioning_tip_de?: string;
  commissioning_tip_nl?: string;
  config_number?: number | null;
  not_applicable_device_parameters?: string[];
  advanced_device_parameters?: string[];
  default_parameters?: Array<{ field_name: string; value: unknown }>;
};

type DeviceValueFieldDef = {
  type: string;
  key?: string;
  data_type?: string;
};

const formatDefaultValueForForm = (
  fieldDef: { data_type?: string },
  defaultValue: unknown
): string => {
  if (fieldDef.data_type === "number") {
    return defaultValue != null ? defaultValue.toString() : "";
  }
  if (fieldDef.data_type === "boolean") {
    return defaultValue ? "true" : "false";
  }
  return defaultValue != null ? defaultValue.toString() : "";
};

const DROPOUT_PHASE_TABLE_COLUMNS = [
  { key: "L1" as const, label: "device_settings_dropout_phase_table_l1" },
  { key: "L2" as const, label: "device_settings_dropout_phase_table_l2" },
  { key: "L3" as const, label: "device_settings_dropout_phase_table_l3" },
];

const DROPOUT_PHASE_TABLE_ROWS = [
  {
    label: "device_settings_field_dropout_v_max",
    tooltip: "device_settings_field_dropout_v_max_tooltip",
    unit: "V",
    keys: {
      L1: "dropout_v_max",
      L2: "dropout_v_max_l2",
      L3: "dropout_v_max_l3",
    },
  },
  {
    label: "device_settings_field_dropout_v_threshold",
    tooltip: "device_settings_field_dropout_v_threshold_tooltip",
    unit: "V",
    keys: {
      L1: "dropout_v_threshold",
      L2: "dropout_v_threshold_l2",
      L3: "dropout_v_threshold_l3",
    },
  },
  {
    label: "device_settings_field_dropout_power",
    tooltip: "device_settings_field_dropout_power_tooltip",
    unit: "W",
    keys: {
      L1: "dropout_power",
      L2: "dropout_power_l2",
      L3: "dropout_power_l3",
    },
  },
];

const getAllDeviceValueFieldDefs = (
  device: Device,
  parameterConfigs: ParameterConfig[]
): DeviceValueFieldDef[] =>
  getDeviceData(device, false, parameterConfigs, false).filter(
    (item) => item.type === "value" && !!item.key
  ) as DeviceValueFieldDef[];

const buildDeviceWithParameterConfiguration = (
  device: Device,
  config: ParameterConfig | undefined
): Device => {
  if (!config) return device;
  return {
    ...device,
    parameter_configuration: {
      id: config.id,
      name: config.name,
      not_applicable_device_parameters: config.not_applicable_device_parameters ?? [],
      advanced_device_parameters: config.advanced_device_parameters ?? [],
    },
  };
};

const getParameterConfigurationFromForm = (
  formData: Record<string, string> | null,
  parameterConfigs: ParameterConfig[]
): ParameterConfig | undefined => {
  const configId = formData?.parameter_configuration;
  if (!configId) return undefined;
  return parameterConfigs.find((config) => config.id.toString() === configId);
};

const getDeviceData = (
  device: Device,
  hideAdvancedParams: boolean = true,
  parameterConfigs: ParameterConfig[] = [],
  hideNotApplicableParams: boolean = true,
  parameterConfigurationOverride?: Pick<
    ParameterConfig,
    "not_applicable_device_parameters" | "advanced_device_parameters"
  > | null
) => {
  const allDeviceData = [
    {
      type: "section",
      title: "device_settings_section_installation",
    },
    {
      type: "value",
      key: "installation_type",
      data_type: "select",
      label: "device_settings_field_installation_type",
      tooltip: "device_settings_field_installation_type_tooltip",
      values: [
        {
          key: "WATER_HEATER_MODE",
          label: "device_settings_field_installation_type_water_heater_mode",
        },
        {
          key: "HEATER_MODE",
          label: "device_settings_field_installation_type_heater_mode",
        },
      ],
    },
    {
      type: "value",
      key: "parameter_configuration",
      data_type: "select",
      label: "device_settings_field_parameter_configuration",
      tooltip: "device_settings_field_parameter_configuration_tooltip",
      values: filterConfigsForModel(
        parameterConfigs,
        device.soliseco_model,
        device.parameter_configuration?.id
      ).map(config => ({
        key: config.id.toString(),
        label: formatConfigOptionLabel(config),
      })),
    },
    {
      type: "section",
      title: "device_settings_section_configuration",
    },
    {
      type: "value",
      key: "mode",
      data_type: "select",
      label: "device_settings_field_mode",
      tooltip: "device_settings_field_mode_tooltip",
      values: [
        {
          key: "SOLO",
          label: "device_settings_field_mode_solo",
        },
        {
          key: "DUO",
          label: "device_settings_field_mode_duo",
        },
      ],
    },
    {
      type: "value",
      key: "running_state",
      data_type: "select",
      label: "device_settings_field_running_state",
      tooltip: "device_settings_field_running_state_tooltip",
      values: [
        {
          key: "BOOST",
          label: "device_settings_field_running_state_boost",
        },
        {
          key: "NORMAL",
          label: "device_settings_field_running_state_normal",
        },
        {
          key: "SLEEP",
          label: "device_settings_field_running_state_sleep",
        },
      ],
    },
    {
      type: "value",
      key: "water_heater_min_power",
      data_type: "number",
      label: "device_settings_field_water_heater_min_power",
      tooltip: "device_settings_field_water_heater_min_power_tooltip",
      unit: "W",
      min: 0,
      max: (device as any)?.max_power_limit ?? 3600,
    },
    {
      type: "value",
      key: "water_heater_max_power",
      data_type: "number",
      label: "device_settings_field_water_heater_max_power",
      tooltip: "device_settings_field_water_heater_max_power_tooltip",
      unit: "W",
      min: 0,
      max: (device as any)?.max_power_limit ?? 3600,
    },
    {
      type: "value",
      key: "max_installation_power",
      data_type: "number",
      label: "device_settings_field_max_installation_power",
      tooltip: "device_settings_field_max_installation_power_tooltip",
      unit: "W",
      allow_float: true,
      min: 0,
      max: (device as any)?.max_power_limit ?? 3600,
    },
    {
      type: "value",
      key: "boost_percentage",
      data_type: "number",
      label: "device_settings_field_boost_percentage",
      tooltip: "device_settings_field_boost_percentage_tooltip",
      unit: "%",
    },
    {
      type: "value",
      key: "meter_factor",
      data_type: "number",
      label: "device_settings_field_meter_factor",
      tooltip: "device_settings_field_meter_factor_tooltip",
      allow_float: true,
      min: -10000,
      max: 10000,
    },
    {
      type: "value",
      key: "valve_position",
      data_type: "select",
      label: "device_settings_field_valve_position",
      tooltip: "device_settings_field_valve_position_tooltip",
      getValues: (device: Device) => {
        const configNumber = device?.parameter_configuration?.name
          ? parseInt(device.parameter_configuration.name.match(/Config\s+(\d+)/i)?.[1] ?? "0", 10)
          : 0;
        if ([2, 32, 62].includes(configNumber)) {
          return [
            { key: "HYBRID", label: "device_settings_field_valve_position_hybrid" },
            { key: "FULL_SOLAR", label: "device_settings_field_valve_position_full_solar" },
          ];
        }
        if ([8, 38, 68].includes(configNumber)) {
          return [
            { key: "WATER_HEATER", label: "device_settings_field_valve_position_water_heater_mode" },
            { key: "HEATER", label: "device_settings_field_valve_position_heater_mode" },
          ];
        }
        return [
          { key: "WATER_HEATER", label: "device_settings_field_valve_position_water_heater_mode" },
          { key: "HEATER", label: "device_settings_field_valve_position_heater_mode" },
          { key: "FULL_SOLAR", label: "device_settings_field_valve_position_full_solar" },
          { key: "HYBRID", label: "device_settings_field_valve_position_hybrid" },
        ];
      },
    },
    {
      type: "value",
      key: "circulator_deadtime_min",
      data_type: "number",
      label: "device_settings_field_circulator_deadtime_min",
      tooltip: "device_settings_field_circulator_deadtime_min_tooltip",
      unit: "min",
      min: 10,
      max: 1440,
    },
    {
      type: "value",
      key: "circulator_power_percent",
      data_type: "number",
      label: "device_settings_field_circulator_power",
      tooltip: "device_settings_field_circulator_power_tooltip",
      unit: "%",
      min: 0,
      max: 200,
    },
    {
      type: "value",
      key: "circulator_temperature",
      data_type: "number",
      label: "device_settings_field_circulator_temperature",
      tooltip: "device_settings_field_circulator_temperature_tooltip",
      unit: "°C",
    },
    {
      type: "value",
      key: "valve_temperature_limit",
      data_type: "number",
      label: "device_settings_field_valve_temperature_limit",
      tooltip: "device_settings_field_valve_temperature_limit_tooltip",
      unit: "°C",
    },
    {
      type: "value",
      key: "heater_max_temp",
      data_type: "number",
      label: "device_settings_field_heater_max_temp",
      tooltip: "device_settings_field_heater_max_temp_tooltip",
      unit: "°C",
      min: 5,
      allow_float: true,
    },
    {
      type: "value",
      key: "duration_temperature_after_circulation_min",
      data_type: "number",
      label: "device_settings_field_duration_temperature_after_circulation",
      tooltip: "device_settings_field_duration_temperature_after_circulation_tooltip",
    },
    {
      type: "section",
      title: "device_settings_section_sanitization",
    },
    {
      type: "value",
      key: "sanitation_active",
      data_type: "boolean",
      label: "device_settings_field_sanitation_active",
      tooltip: "device_settings_field_sanitation_active_tooltip",
    },
    {
      type: "value",
      key: "sanitation_max_period_days",
      data_type: "number",
      label: "device_settings_field_sanitation_max_period",
      tooltip: "device_settings_field_sanitation_max_period_tooltip",
      unit: "days",
      visibleIf: (device: Device, currentFormData: any) => {
        return currentFormData.sanitation_active === "true";
      },
    },
    {
      type: "value",
      key: "sanitation_hour_start_time",
      data_type: "number",
      label: "device_settings_field_sanitation_hour_start_time",
      tooltip: "device_settings_field_sanitation_hour_start_time_tooltip",
      visibleIf: (device: Device, currentFormData: any) => {
        return currentFormData.sanitation_active === "true";
      },
    },
    {
      type: "value",
      key: "sanitation_temperature",
      data_type: "number",
      label: "device_settings_field_sanitation_temperature",
      tooltip: "device_settings_field_sanitation_temperature_tooltip",
      unit: "°C",
      visibleIf: (device: Device, currentFormData: any) => {
        return currentFormData.sanitation_active === "true";
      },
    },
    {
      type: "section",
      title: "device_settings_section_general_data",
    },
    {
      type: "value",
      key: "name",
      data_type: "string",
      label: "device_settings_field_device_name",
    },
    {
      type: "value",
      key: "device_id",
      data_type: "string",
      label: "device_settings_field_device_id",
      editable: false,
    },
    {
      type: "value",
      key: "own_address_ip",
      data_type: "string",
      label: "device_settings_field_own_address_ip",
      editable: false,
    },
    {
      type: "section",
      title: "Dropout",
    },
    {
      type: "value",
      key: "handle_dropout",
      data_type: "boolean",
      label: "device_settings_field_handle_dropout",
      tooltip: "device_settings_field_handle_dropout_tooltip",
    },
    {
      type: "value",
      key: "installation_phase",
      data_type: "number",
      label: "device_settings_field_installation_phase",
      tooltip: "device_settings_field_installation_phase_tooltip",
      editable: false,
      visibleIf: (device: Device, currentFormData: any) => {
        return currentFormData.handle_dropout === "true";
      },
    },
    {
      type: "warning",
      message: "device_settings_incorrect_phase_devices",
      isActive: (device: Device, currentFormData: any) => {
        return (
          currentFormData.handle_dropout === "true" &&
          device.dropout_phase_correct === false
        );
      },
    },
    {
      type: "value",
      key: "dropout_period_start_time",
      data_type: "string",
      label: "device_settings_field_dropout_period_start",
      tooltip: "device_settings_field_dropout_period_start_tooltip",
      visibleIf: (device: Device, currentFormData: any) => {
        return currentFormData.handle_dropout === "true";
      },
    },
    {
      type: "value",
      key: "dropout_t_max",
      data_type: "number",
      label: "device_settings_field_dropout_t_max",
      tooltip: "device_settings_field_dropout_t_max_tooltip",
      unit: "°C",
      visibleIf: (device: Device, currentFormData: any) => {
        return currentFormData.handle_dropout === "true";
      },
    },
    {
      type: "dropout_phase_table",
      visibleIf: (device: Device, currentFormData: any) => {
        return currentFormData.handle_dropout === "true" && isTriPhaseDevice(device);
      },
    },
    {
      type: "value",
      key: "dropout_v_max",
      data_type: "number",
      allow_float: true,
      label: "device_settings_field_dropout_v_max",
      tooltip: "device_settings_field_dropout_v_max_tooltip",
      unit: "V",
      visibleIf: (device: Device, currentFormData: any) => {
        return currentFormData.handle_dropout === "true" && !isTriPhaseDevice(device);
      },
    },    
    {
      type: "value",
      key: "dropout_v_threshold",
      data_type: "number",
      allow_float: true,
      label: "device_settings_field_dropout_v_threshold",
      tooltip: "device_settings_field_dropout_v_threshold_tooltip",
      unit: "V",
      visibleIf: (device: Device, currentFormData: any) => {
        return currentFormData.handle_dropout === "true" && !isTriPhaseDevice(device);
      },
    },
    {
      type: "value",
      key: "dropout_power",
      data_type: "number",
      label: "device_settings_field_dropout_power",
      tooltip: "device_settings_field_dropout_power_tooltip",
      unit: "W",
      visibleIf: (device: Device, currentFormData: any) => {
        return currentFormData.handle_dropout === "true" && !isTriPhaseDevice(device);
      },
    },
    {
      type: "value",
      key: "dropout_v_max_l2",
      data_type: "number",
      allow_float: true,
      label: "device_settings_field_dropout_v_max_l2",
      tooltip: "device_settings_field_dropout_v_max_l2_tooltip",
      unit: "V",
      visibleIf: () => false,
    },
    {
      type: "value",
      key: "dropout_v_threshold_l2",
      data_type: "number",
      allow_float: true,
      label: "device_settings_field_dropout_v_threshold_l2",
      tooltip: "device_settings_field_dropout_v_threshold_l2_tooltip",
      unit: "V",
      visibleIf: () => false,
    },
    {
      type: "value",
      key: "dropout_power_l2",
      data_type: "number",
      allow_float: true,
      label: "device_settings_field_dropout_power_l2",
      tooltip: "device_settings_field_dropout_power_l2_tooltip",
      unit: "W",
      visibleIf: () => false,
    },
    {
      type: "value",
      key: "dropout_v_max_l3",
      data_type: "number",
      allow_float: true,
      label: "device_settings_field_dropout_v_max_l3",
      tooltip: "device_settings_field_dropout_v_max_l3_tooltip",
      unit: "V",
      visibleIf: () => false,
    },
    {
      type: "value",
      key: "dropout_v_threshold_l3",
      data_type: "number",
      allow_float: true,
      label: "device_settings_field_dropout_v_threshold_l3",
      tooltip: "device_settings_field_dropout_v_threshold_l3_tooltip",
      unit: "V",
      visibleIf: () => false,
    },
    {
      type: "value",
      key: "dropout_power_l3",
      data_type: "number",
      allow_float: true,
      label: "device_settings_field_dropout_power_l3",
      tooltip: "device_settings_field_dropout_power_l3_tooltip",
      unit: "W",
      visibleIf: () => false,
    },
    {
      type: "value",
      key: "dropout_period_end_time",
      data_type: "string",
      label: "device_settings_field_dropout_period_end",
      tooltip: "device_settings_field_dropout_period_end_tooltip",
      visibleIf: (device: Device, currentFormData: any) => {
        return currentFormData.handle_dropout === "true";
      },
    },
    {
      type: "section",
      title: "device_settings_section_temperatures",
    },
    {
      type: "value",
      key: "t_cold",
      data_type: "number",
      label: "device_settings_field_t_cold",
      unit: "°C",
      min: -5,
    },
    {
      type: "value",
      key: "t_lukewarm",
      data_type: "number",
      label: "device_settings_field_t_lukewarm",
      unit: "°C",
    },
    {
      type: "value",
      key: "t_hot",
      data_type: "number",
      label: "device_settings_field_t_hot",
      unit: "°C",
    },
    {
      type: "value",
      key: "t_very_hot",
      data_type: "number",
      label: "device_settings_field_t_very_hot",
      unit: "°C",
    },
    {
      type: "value",
      key: "t_max",
      data_type: "number",
      label: "device_settings_field_t_max",
      unit: "°C",
    },
    {
      type: "value",
      key: "use_filtered_temperature",
      data_type: "boolean",
      label: "device_settings_field_use_filtered_temperature",
      tooltip: "device_settings_field_use_filtered_temperature_tooltip",
    },
    {
      type: "section",
      title: "device_settings_section_firmware",
    },
    {
      type: "value",
      key: "firmware_automatic_update",
      data_type: "boolean",
      label: "device_settings_field_firmware_automatic_update",
      tooltip: "device_settings_field_firmware_automatic_update_tooltip",
    },
  ];

  const notApplicableParams: string[] = parameterConfigurationOverride
    ? parameterConfigurationOverride.not_applicable_device_parameters ?? []
    : (device as any).not_applicable_device_parameters ||
      (device.parameter_configuration as any)?.not_applicable_device_parameters ||
      [];

  const filteredByApplicability =
    hideNotApplicableParams && notApplicableParams.length > 0
      ? allDeviceData.filter((item) => {
          if (item.type === "section" || !item.key) return true;
          return !notApplicableParams.includes(item.key);
        })
      : allDeviceData;

  if (hideAdvancedParams) {
    const advancedParams = parameterConfigurationOverride
      ? parameterConfigurationOverride.advanced_device_parameters ?? []
      : (device as any).advanced_device_parameters ||
        device.parameter_configuration?.advanced_device_parameters ||
        [];
    return filteredByApplicability.filter((item) => {
      if (item.type === "section" || !item.key) return true;
      return !advancedParams.includes(item.key);
    });
  }

  return filteredByApplicability;
};

export const generate_from_device = (format: any, device: any) => {
  const values = format.filter((e: any) => e.type === "value");
  const state: any = {};
  for (let index = 0; index < values.length; index++) {
    const value = values[index];
    if (value.key === "parameter_configuration") {
      if (device.parameter_configuration && device.parameter_configuration.id) {
        state[value.key] = device.parameter_configuration.id.toString();
      } else {
        state[value.key] = "";
      }
    } else {
      state[value.key] =
        device[value.key] != null ? device[value.key].toString() : "";
    }
  }

  return state;
};

export const generate_for_device = (format: any, data: any) => {
  const values = format.filter((e: any) => e.type === "value");
  const state: any = {};

  for (let index = 0; index < values.length; index++) {
    const value = values[index];

    if (value.data_type === "number") {
      const raw = data[value.key];
      if (raw === "" || raw == null) {
        state[value.key] = null;
      } else if (value.allow_float === true) {
        const num = parseFloat(raw);
        state[value.key] = Number.isNaN(num) ? null : num;
      } else {
        const num = parseInt(raw, 10);
        state[value.key] = Number.isNaN(num) ? null : num;
      }
    } else if (value.data_type === "boolean") {
      state[value.key] = data[value.key] === "true" ? true : false;
    } else if (value.key === "parameter_configuration") {
      state[value.key] = parseInt(data[value.key]);
    } else if (value.key === "master_device") {
      state[value.key] = data[value.key] === "" ? null : parseInt(data[value.key]);
    } else {
      state[value.key] = data[value.key];
    }
  }
  return state;
};

const DeviceSettings = ({
  device,
  updateDevice,
  deleteDevice,
}: {
  device: Device;
  updateDevice: any;
  deleteDevice: any;
}) => {
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [formData, setFormData] = useState<any | null>(null);
  const [parameterConfigs, setParameterConfigs] = useState<ParameterConfig[]>([]);
  const base_data = useRef<string | null>(null);
  const { t, i18n } = useTranslation(["settings", "messages"]);
  const { notification } = App.useApp();
  const [errors, setErrors] = useState<string[]>([]);
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  const isBasicUser = !user.is_installer && !user.is_advanced_user;
  const [createHomeOpen, setCreateHomeOpen] = useState(false);
  const [createDeviceOpen, setCreateDeviceOpen] = useState(false);
  const isAdminOrStaff = user.is_staff || user.is_superuser;
  const hideNotApplicableParams = true;
  const hideAdvancedParams = isBasicUser && !isAdminOrStaff;
  const [commissioningTipOpen, setCommissioningTipOpen] = useState(false);
  const [configChangeModalOpen, setConfigChangeModalOpen] = useState(false);
  const [pendingConfigChange, setPendingConfigChange] = useState<{
    newConfigId: string;
    newConfigName: string;
    applyDefaults: boolean | null;
  } | null>(null);
  const previousConfigId = useRef<string | null>(null);
  const [configureWiFiOpen, setConfigureWiFiOpen] = useState(false);
  const valvePositionTelemetryRef = useRef<string | null>(null);
  const valvePositionUpdatedAtRef = useRef<number | null>(null);
  const valvePositionDirtyRef = useRef(false);
  const [valveTelemetryUpdatedAt, setValveTelemetryUpdatedAt] = useState<number | null>(null);
  const [telemetryDisplayRevision, setTelemetryDisplayRevision] = useState(0);
  const [valveTelemetryRefreshing, setValveTelemetryRefreshing] = useState(false);
  const [valveStaleModalOpen, setValveStaleModalOpen] = useState(false);

  const applyTelemetryValvePosition = (data: Record<string, string>) => {
    if (valvePositionTelemetryRef.current) {
      data.valve_position = valvePositionTelemetryRef.current;
    }
    return data;
  };

  const buildFormStateFromDevice = (deviceSource: Device) => {
    const data = generate_from_device(
      getDeviceData(deviceSource, hideAdvancedParams, parameterConfigs, hideNotApplicableParams),
      deviceSource
    );
    delete data.valve_position;
    return applyTelemetryValvePosition(data);
  };

  const syncBaseDataValvePosition = (position: string) => {
    if (!base_data.current) return;
    const base = JSON.parse(base_data.current);
    base.valve_position = position;
    base_data.current = JSON.stringify(base);
  };

  const applyTelemetrySnapshotToForm = (
    snapshot: ValveTelemetrySnapshot,
    syncBase = false
  ) => {
    if (valvePositionDirtyRef.current || !snapshot.position) return;

    setFormData((prev: Record<string, string> | null) => {
      if (!prev) return prev;
      if (prev.valve_position === snapshot.position) return prev;
      return { ...prev, valve_position: snapshot.position as string };
    });

    if (syncBase && snapshot.position) {
      syncBaseDataValvePosition(snapshot.position);
    }
  };

  const fetchValveTelemetry = async (
    deviceId: number,
    options?: { bypassCache?: boolean }
  ): Promise<ValveTelemetrySnapshot> => {
    const empty: ValveTelemetrySnapshot = { position: null, updatedAt: null, fresh: false };
    try {
      const response = await axios.get(`/api-v1/device/${deviceId}/last/`, {
        ...(options?.bypassCache ? { params: { _: Date.now() } } : {}),
      });
      const { valve_position, valve_position_updated_at } = response.data;
      const position = valve_position ? String(valve_position) : null;
      const updatedAt =
        valve_position_updated_at != null ? Number(valve_position_updated_at) : null;

      valvePositionTelemetryRef.current = position;
      valvePositionUpdatedAtRef.current = updatedAt;
      setValveTelemetryUpdatedAt(updatedAt);
      setTelemetryDisplayRevision((n) => n + 1);

      const fresh = isValveTelemetryFreshAt(updatedAt);
      return { position, updatedAt, fresh };
    } catch (error) {
      console.error("Failed to fetch valve position telemetry:", error);
      valvePositionTelemetryRef.current = null;
      valvePositionUpdatedAtRef.current = null;
      setValveTelemetryUpdatedAt(null);
      setTelemetryDisplayRevision((n) => n + 1);
      return empty;
    }
  };

  const getValveTelemetryStatusLabel = (updatedAt: number | null) => {
    if (updatedAt == null) {
      return t("settings:device_settings_valve_position_telemetry_unknown");
    }
    const minutes = getValveTelemetryAgeMinutes(updatedAt);
    if (isValveTelemetryFreshAt(updatedAt)) {
      if (minutes === 0) {
        return t("settings:device_settings_valve_position_telemetry_fresh_now");
      }
      return t("settings:device_settings_valve_position_telemetry_fresh", { minutes });
    }
    return t("settings:device_settings_valve_position_telemetry_stale", { minutes });
  };

  const hasNonValveFormChanges = () => {
    if (!formData || !base_data.current) return false;
    const current = { ...formData };
    const base = JSON.parse(base_data.current);
    delete current.valve_position;
    delete base.valve_position;
    return JSON.stringify(current) !== JSON.stringify(base);
  };

  const isFormDirty = () => {
    if (!formData || !base_data.current) return false;
    if (pendingConfigChange?.applyDefaults === true) return true;
    if (valvePositionDirtyRef.current) return true;
    return hasNonValveFormChanges();
  };

  const refreshValveTelemetry = async (showFeedback = true) => {
    setValveTelemetryRefreshing(true);
    const snapshot = await fetchValveTelemetry(device.id, { bypassCache: true });
    applyTelemetrySnapshotToForm(snapshot, true);
    setValveTelemetryRefreshing(false);
    if (!showFeedback) return snapshot;

    if (snapshot.fresh) {
      notification.success({
        message: t("settings:device_settings_valve_position_refresh_success_title"),
        description: t("settings:device_settings_valve_position_refresh_success_fresh"),
        placement: "topLeft",
      });
    } else if (snapshot.updatedAt != null) {
      notification.warning({
        message: t("settings:device_settings_valve_position_refresh_success_title"),
        description: t("settings:device_settings_valve_position_refresh_still_stale"),
        placement: "topLeft",
      });
    } else {
      notification.warning({
        message: t("settings:device_settings_valve_position_refresh_success_title"),
        description: t("settings:device_settings_valve_position_telemetry_unknown"),
        placement: "topLeft",
      });
    }
    return snapshot;
  };

  useEffect(() => {
    const query = device.soliseco_model
      ? `?soliseco_model=${encodeURIComponent(device.soliseco_model)}`
      : "";
    axios
      .get(`/api-v1/config/parameter-configurations/${query}`)
      .then((response) => {
        const current = device.parameter_configuration
          ? {
              id: device.parameter_configuration.id,
              name: device.parameter_configuration.name,
              commissioning_tip_en: device.parameter_configuration.commissioning_tip_en,
              commissioning_tip_fr: device.parameter_configuration.commissioning_tip_fr,
              commissioning_tip_de: device.parameter_configuration.commissioning_tip_de,
              commissioning_tip_nl: device.parameter_configuration.commissioning_tip_nl,
              not_applicable_device_parameters:
                device.parameter_configuration.not_applicable_device_parameters,
              advanced_device_parameters:
                device.parameter_configuration.advanced_device_parameters,
            }
          : null;
        setParameterConfigs(mergeCurrentConfig(response.data, current));
      })
      .catch((error) => {
        console.error("Failed to fetch parameter configurations:", error);
      });
  }, [device.soliseco_model, device.parameter_configuration?.id]);

  useEffect(() => {
    setErrors([]);
  }, [formData]);

  useEffect(() => {
    let cancelled = false;
    const initForm = async () => {
      await fetchValveTelemetry(device.id, { bypassCache: true });
      if (cancelled) return;
      const data = buildFormStateFromDevice(device);
      valvePositionDirtyRef.current = false;
      setFormData(data);
      setErrors([]);
      base_data.current = JSON.stringify(data);
      if (device.parameter_configuration?.id) {
        previousConfigId.current = device.parameter_configuration.id.toString();
      }
    };
    initForm();
    return () => {
      cancelled = true;
    };
  }, [device.id, hideAdvancedParams, parameterConfigs, hideNotApplicableParams]);

  useEffect(() => {
    if (!formData) return;

    const intervalId = window.setInterval(
      () => void refreshValveTelemetry(false),
      VALVE_TELEMETRY_POLL_INTERVAL_MS
    );
    return () => window.clearInterval(intervalId);
  }, [device.id, formData !== null]);

  useEffect(() => {
    if (!formData || previousConfigId.current === null) return;
    
    const currentConfigId = formData.parameter_configuration;
    const previousId = previousConfigId.current;
    
    const alreadyPendingForCurrent =
      pendingConfigChange != null &&
      pendingConfigChange.newConfigId === currentConfigId;

    if (
      currentConfigId &&
      currentConfigId !== previousId &&
      currentConfigId !== "" &&
      !alreadyPendingForCurrent
    ) {
      const newConfig = parameterConfigs.find(c => c.id.toString() === currentConfigId) as any;
      if (newConfig) {
        setPendingConfigChange({
          newConfigId: currentConfigId,
          newConfigName: newConfig.name,
          applyDefaults: null,
        });
        setConfigChangeModalOpen(true);
      }
    }
  }, [formData?.parameter_configuration, parameterConfigs, pendingConfigChange]);

  const cancel = async () => {
    await fetchValveTelemetry(device.id, { bypassCache: true });
    const data = buildFormStateFromDevice(device);
    valvePositionDirtyRef.current = false;
    setFormData(data);
    setErrors([]);
    base_data.current = JSON.stringify(data);
    setPendingConfigChange(null);
    setConfigChangeModalOpen(false);
    if (device.parameter_configuration?.id) {
      previousConfigId.current = device.parameter_configuration.id.toString();
    } else {
      previousConfigId.current = null;
    }
  };

  const handleConfigChangeDecision = (applyDefaults: boolean) => {
    if (!pendingConfigChange) return;
    
    previousConfigId.current = pendingConfigChange.newConfigId;
    
    if (applyDefaults) {
      const newConfig = parameterConfigs.find(
        c => c.id.toString() === pendingConfigChange.newConfigId
      ) as any;
      
      const defaultParamsDict: Record<string, any> = {};
      if (newConfig?.default_parameters && Array.isArray(newConfig.default_parameters)) {
        newConfig.default_parameters.forEach((param: any) => {
          if (param.field_name) {
            defaultParamsDict[param.field_name] = param.value;
          }
        });
      }
      
      if (Object.keys(defaultParamsDict).length > 0) {
        const updatedFormData = { ...formData };

        updatedFormData.parameter_configuration = pendingConfigChange.newConfigId;

        const allFieldDefs = getAllDeviceValueFieldDefs(device, parameterConfigs);

        Object.keys(defaultParamsDict).forEach((key) => {
          const defaultValue = defaultParamsDict[key];
          const fieldDef = allFieldDefs.find((item) => item.key === key);
          if (!fieldDef) return;
          updatedFormData[key] = formatDefaultValueForForm(fieldDef, defaultValue);
        });

        setFormData(updatedFormData);
      } else {
        setFormData({
          ...formData,
          parameter_configuration: pendingConfigChange.newConfigId,
        });
      }
      
      setPendingConfigChange({
        ...pendingConfigChange,
        applyDefaults: true,
      });
    } else {
      setPendingConfigChange(null);
    }
    
    setConfigChangeModalOpen(false);
  };

  const performSave = async (skipValveChange = false) => {
    setLoading(true);
    let error: any | null = null;
    const valvePositionChanged = !skipValveChange && valvePositionDirtyRef.current;

    const selectedConfig = getParameterConfigurationFromForm(formData, parameterConfigs);
    const deviceForPayload = buildDeviceWithParameterConfiguration(device, selectedConfig);

    const payload = generate_for_device(
      getDeviceData(deviceForPayload, hideAdvancedParams, parameterConfigs, hideNotApplicableParams),
      formData
    );
    delete payload.valve_position;
    if (valvePositionChanged) {
      payload.valve_position_changed = true;
    }

    if (pendingConfigChange?.applyDefaults === true) {
      (payload as any).apply_default_values = true;
    }

    const response = await axios
      .patch(`/api-v1/device/${device.id}/`, payload)
      .then((e) => e.data)
      .catch((e) => {
        error = e.response.data;
        return null;
      });

    if (response) {
      updateDevice({
        id: device.id,
        data: response,
        updated: true,
      });
      notification.success({
        message: t("messages:settings_update_success_message"),
        placement: "topLeft",
      });
      if (valvePositionChanged) {
        Modal.info({
          title: t("messages:valve_position_wait_for_move_title"),
          content: t("messages:valve_position_wait_for_move_description"),
        });
        valvePositionDirtyRef.current = false;
        await fetchValveTelemetry(device.id, { bypassCache: true });
        const refreshedData = buildFormStateFromDevice(response);
        setFormData(refreshedData);
        base_data.current = JSON.stringify(refreshedData);
      } else if (skipValveChange) {
        valvePositionDirtyRef.current = false;
        await fetchValveTelemetry(device.id, { bypassCache: true });
        const revertedData = buildFormStateFromDevice(response);
        setFormData(revertedData);
        base_data.current = JSON.stringify(revertedData);
      } else {
        valvePositionDirtyRef.current = false;
        await fetchValveTelemetry(device.id, { bypassCache: true });
        const syncedData = buildFormStateFromDevice(response);
        setFormData(syncedData);
        base_data.current = JSON.stringify(syncedData);
      }
      setErrors([]);
      setPendingConfigChange(null);
      if (response.parameter_configuration?.id) {
        previousConfigId.current = response.parameter_configuration.id.toString();
      }
    } else {
      if (error.length == undefined) {
        const error_lst = [];
        for (const [key, value] of Object.entries(error)) {
          error_lst.push(key);
          const updated: any = value;
          const error_message = getDeviceData(
            device,
            hideAdvancedParams,
            parameterConfigs,
            hideNotApplicableParams
          ).find(
            (d) => d.key == key
          )?.label;
          if (updated.includes("This field may not be blank.")) {
            notification.error({
              message: t("messages:field_update_error", {
                field_name: t(error_message ? error_message : ""),
              }),
              description: t("messages:field_cannot_be_empty"),
              placement: "topLeft",
            });
          } else if (
            updated.includes("Ensure this value is greater than or equal to 5.")
          ) {
            notification.error({
              message: t("messages:field_update_error", {
                field_name: t(error_message ? error_message : ""),
              }),
              description: t("messages:field_more_than_x", { value: 5 }),
              placement: "topLeft",
            });
          } else if (key === "valve_position") {
            setValveStaleModalOpen(true);
          } else if (key === "water_heater_valve_side") {
            const description = Array.isArray(updated) ? updated.join(" ") : String(updated);
            notification.error({
              message: t("messages:settings_update_error_message"),
              description,
              placement: "topLeft",
            });
          } else if (key == "non_field_errors") {
            notification.error({
              message: t("messages:settings_update_error_message"),
              description: value as string,
              placement: "topLeft",
            });
          } else {
            notification.error({
              message: t("messages:field_update_error", {
                field_name: t(error_message ? error_message : ""),
              }),
              description: t("messages:field_error"),
              placement: "topLeft",
            });
          }
        }
        setErrors(error_lst);
      } else {
        notification.error({
          message: t("messages:settings_update_error_message"),
          description: error[0],
          placement: "topLeft",
        });
      }
    }
    setLoading(false);
  };

  const save = async () => {
    if (valvePositionDirtyRef.current) {
      const snapshot = await fetchValveTelemetry(device.id, { bypassCache: true });
      if (!snapshot.fresh) {
        setValveStaleModalOpen(true);
        return;
      }
    }
    await performSave(false);
  };

  const handleValveStaleRefresh = async () => {
    const snapshot = await refreshValveTelemetry(true);
    if (snapshot.fresh) {
      setValveStaleModalOpen(false);
    }
  };

  const handleSaveWithoutValveChange = async () => {
    setValveStaleModalOpen(false);
    await performSave(true);
  };

  const delete_device = () => {
    setDeleteLoading(true);
    const device_name = device.name;
    deleteDevice(device.id).then(() => {
      setDeleteLoading(false);
      setDeleteOpen(false);
      notification.error({
        message: t("messages:device_deleted_message", {
          device_name: device_name,
        }),
        placement: "topLeft",
      });
      navigate(`/home/${device.home}/settings`);
    });
  };

  if (!formData) return null;

  return (
    <div className="device-home-settings">
      <CreateHomeModal
        open={createHomeOpen}
        close={() => setCreateHomeOpen(false)}
      />
      <CreateDeviceModal
        open={createDeviceOpen}
        device={device.home}
        close={() => setCreateDeviceOpen(false)}
      />
      <ConfigureWiFiModal
        open={configureWiFiOpen}
        device={{ id: device.id, name: device.name }}
        close={() => setConfigureWiFiOpen(false)}
      />
      <CommissioningTipModal
        open={commissioningTipOpen}
        title={
          getParameterConfigurationFromForm(formData, parameterConfigs)?.name ||
          t("device_settings_commissioning_tip_title")
        }
        tip={getCommissioningTipForLanguage(
          getParameterConfigurationFromForm(formData, parameterConfigs),
          i18n.language
        )}
        closeLabel={t("device_settings_commissioning_tip_close")}
        onClose={() => setCommissioningTipOpen(false)}
      />
      <Modal
        open={valveStaleModalOpen}
        title={t("messages:valve_position_stale_modal_title")}
        onCancel={() => setValveStaleModalOpen(false)}
        footer={[
          <Button key="close" onClick={() => setValveStaleModalOpen(false)}>
            {t("main:cancel")}
          </Button>,
          hasNonValveFormChanges() ? (
            <Button key="save-other" onClick={handleSaveWithoutValveChange}>
              {t("messages:valve_position_save_without_valve")}
            </Button>
          ) : null,
          <Button
            key="refresh"
            type="primary"
            loading={valveTelemetryRefreshing}
            onClick={handleValveStaleRefresh}
          >
            {t("settings:device_settings_valve_position_refresh_telemetry")}
          </Button>,
        ]}
      >
        <p>{t("messages:valve_position_stale_modal_body")}</p>
        <p className="valve-telemetry-modal-status">
          {telemetryDisplayRevision >= 0 &&
            getValveTelemetryStatusLabel(valveTelemetryUpdatedAt)}
        </p>
      </Modal>
      <Modal
        open={configChangeModalOpen}
        title={t("device_settings_config_change_title", "Change Configuration")}
        onCancel={() => {
          if (formData && previousConfigId.current) {
            setFormData({
              ...formData,
              parameter_configuration: previousConfigId.current,
            });
          }
          setConfigChangeModalOpen(false);
          setPendingConfigChange(null);
        }}
        footer={[
          <Button
            key="keep"
            onClick={() => handleConfigChangeDecision(false)}
          >
            {t("device_settings_keep_stored_values", "Keep Stored Values")}
          </Button>,
          (() => {
            const newConfig = parameterConfigs.find(
              c => c.id.toString() === pendingConfigChange?.newConfigId
            ) as any;
            const hasDefaults = newConfig?.default_parameters && 
              Array.isArray(newConfig.default_parameters) && 
              newConfig.default_parameters.length > 0;
            
            if (hasDefaults) {
              return (
                <Button
                  key="defaults"
                  type="primary"
                  onClick={() => handleConfigChangeDecision(true)}
                >
                  {t("device_settings_use_default_values", "Use Default Values")}
                </Button>
              );
            }
            return null;
          })(),
        ]}
      >
        <p>
          {t(
            "device_settings_config_change_message",
            "You are changing the configuration to '{{configName}}'.",
            { configName: pendingConfigChange?.newConfigName || "" }
          )}
        </p>
        {(() => {
          const newConfig = parameterConfigs.find(
            c => c.id.toString() === pendingConfigChange?.newConfigId
          ) as any;
          const hasDefaults = newConfig?.default_parameters && 
            Array.isArray(newConfig.default_parameters) && 
            newConfig.default_parameters.length > 0;
          
          if (hasDefaults) {
            return (
              <p>
                {t(
                  "device_settings_config_change_question",
                  "Do you want to keep your current stored values or use the default values of the new configuration?"
                )}
              </p>
            );
          } else {
            return (
              <p>
                {t(
                  "device_settings_config_change_no_defaults",
                  "This configuration does not have default values. Your current stored values will be kept."
                )}
              </p>
            );
          }
        })()}
      </Modal>
      <div className="save-header">
        {isBasicUser && (
          <div className="secondary-actions-group">
            <button
              type="button"
              className="inline-action-btn"
              onClick={() => setCreateHomeOpen(true)}
            >
              <span className="icon-wrapper">
                <NewHome />
              </span>
              <span>{t("home:new_home_modal_title")}</span>
            </button>
            <button
              type="button"
              className="inline-action-btn"
              onClick={() => setCreateDeviceOpen(true)}
            >
              <span className="icon-wrapper">
                <NewDevice />
              </span>
              <span>{t("home:new_device_modal_title")}</span>
            </button>
          </div>
        )}
        <div className="main-btn-wrapper">
          <MainBtn
            loading={loading}
            disabled={!isFormDirty()}
            title={t("save_updates")}
            onClick={save}
          />
          <MainBtn
            title={t("main:cancel")}
            color="secondary"
            onClick={cancel}
          />
        </div>
      </div>
      <div className="device-home-container">
        <div className="settings-title">
          <span>{t("settings_of_the_device", { name: device.name })}</span>
          <div className="row">
            <Button
              onClick={() =>
                navigate(
                  `/home/${device.home}/${device.id}?current-view=dashboard`
                )
              }
            >
              {t("dashboard")}
            </Button>
            {formData.mode === "SOLO" && (
              <Button
                onClick={() =>
                  navigate(`/home/${device.home}/${device.id}?current-view=plan`)
                }
              >
                {t("plan")}
              </Button>
            )}
          </div>
        </div>
        {getDeviceData(
          device,
          hideAdvancedParams,
          parameterConfigs,
          hideNotApplicableParams
        ).map((data: any, index: number) => {
          if (
            data.type === "warning" &&
            data.isActive(device, formData) === true
          ) {
            return (
              <div key={index} className="section-warning">
                <WarningOutlined
                  style={{ fontSize: 18, marginRight: "0.5rem" }}
                />
                <span>{t(data.message, { installation_phase: device.installation_phase })}</span>
              </div>
            );
          }
          if (data.type === "section") {
            return (
              <div key={index} className="section-title">
                <span>{t(data.title)}</span>
              </div>
            );
          }
          if (data.type === "dropout_phase_table") {
            if (data.visibleIf && !data.visibleIf(device, formData)) {
              return null;
            }
            return (
              <div key={index} className="dropout-phase-table-wrap">
                <table className="dropout-phase-table">
                  <thead>
                    <tr>
                      <th />
                      {DROPOUT_PHASE_TABLE_COLUMNS.map((col) => (
                        <th key={col.key}>{t(col.label)}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {DROPOUT_PHASE_TABLE_ROWS.map((row) => (
                      <tr key={row.label}>
                        <th>
                          <span>
                            {t(row.label)}
                            {row.tooltip && (
                              <Tooltip title={t(row.tooltip)}>
                                <InfoCircleOutlined
                                  style={{
                                    color: "#012124",
                                    opacity: 0.4,
                                    marginLeft: "5px",
                                  }}
                                />
                              </Tooltip>
                            )}
                          </span>
                        </th>
                        {DROPOUT_PHASE_TABLE_COLUMNS.map((col) => {
                          const fieldKey = row.keys[col.key];
                          return (
                            <td key={fieldKey}>
                              <div className="input-container">
                                <Input
                                  status={errors.includes(fieldKey) ? "error" : ""}
                                  value={formData[fieldKey]}
                                  onChange={(e) =>
                                    setFormData({
                                      ...formData,
                                      [fieldKey]: e.target.value,
                                    })
                                  }
                                  type="number"
                                  min={0}
                                />
                                {row.unit && <span className="unit">{row.unit}</span>}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          }
          if (data.type === "value" && data.visibleIf && !data.visibleIf(device, formData)) {
            return null;
          }
          if (data.data_type === "string") {
            return (
              <div key={index} className="setting-block">
                <span>
                  {t(data.label)}
                  {data.tooltip && (
                    <Tooltip title={t(data.tooltip)}>
                      <InfoCircleOutlined
                        style={{
                          color: "#012124",
                          opacity: 0.4,
                          marginLeft: "5px",
                        }}
                      />
                    </Tooltip>
                  )}
                </span>
                <div className="input-container">
                  <Input
                    status={errors.includes(data.key) ? "error" : ""}
                    value={formData[data.key]}
                    onChange={(e) =>
                      setFormData({ ...formData, [data.key]: e.target.value })
                    }
                    disabled={data.editable == false}
                  />
                  {data.unit && <span className="unit">{data.unit}</span>}
                </div>
              </div>
            );
          } else if (data.data_type === "number") {
            const min = typeof data.min === "function" ? data.min(device) : data.min;
            let max = typeof data.max === "function" ? data.max(device) : data.max;
            if (data.key === "circulator_power_percent") {
              const selectedConfig = getParameterConfigurationFromForm(formData, parameterConfigs);
              max = circulatorPowerPercentMax(
                selectedConfig?.name ?? device.parameter_configuration?.name
              );
            }
            return (
              <div key={index} className="setting-block">
                <span>
                  {t(data.label)}
                  {data.tooltip && (
                    <Tooltip title={t(data.tooltip)}>
                      <InfoCircleOutlined
                        style={{
                          color: "#012124",
                          opacity: 0.4,
                          marginLeft: "5px",
                        }}
                      />
                    </Tooltip>
                  )}
                </span>
                <div className="input-container">
                  <Input
                    status={errors.includes(data.key) ? "error" : ""}
                    value={formData[data.key]}
                    onChange={(e) =>
                      setFormData({ ...formData, [data.key]: e.target.value })
                    }
                    disabled={data.editable == false}
                    type="number"
                    min={min}
                    max={max}
                  />
                  {data.unit && <span className="unit">{data.unit}</span>}
                </div>
              </div>
            );
          } else if (data.data_type === "select" && (data.values || data.getValues)) {
            const selectValues = data.getValues ? data.getValues(device) : data.values;
            const isValvePositionField = data.key === "valve_position";
            const isParameterConfigField = data.key === "parameter_configuration";
            const selectedConfig = isParameterConfigField
              ? getParameterConfigurationFromForm(formData, parameterConfigs)
              : undefined;
            const commissioningTip = getCommissioningTipForLanguage(
              selectedConfig,
              i18n.language
            );
            const valveTelemetryFresh = isValveTelemetryFreshAt(valveTelemetryUpdatedAt);
            return (
              <div
                key={index}
                className={`setting-block${isValvePositionField ? " setting-block--valve-position" : ""}`}
              >
                <span>
                  {t(data.label)}
                  {data.tooltip && (
                    <Tooltip title={t(data.tooltip)}>
                      <InfoCircleOutlined
                        style={{
                          color: "#012124",
                          opacity: 0.4,
                          marginLeft: "5px",
                        }}
                      />
                    </Tooltip>
                  )}
                </span>
                <div className="input-container">
                  <div className="select-with-telemetry">
                    <Select
                      value={formData[data.key]}
                      onChange={(e) => {
                        if (isValvePositionField) {
                          valvePositionDirtyRef.current = true;
                        }
                        setFormData({ ...formData, [data.key]: e });
                      }}
                      className={
                        isParameterConfigField
                          ? "parameter-configuration-select"
                          : undefined
                      }
                      popupClassName={
                        isParameterConfigField
                          ? "parameter-configuration-select-dropdown"
                          : undefined
                      }
                      popupMatchSelectWidth={isParameterConfigField ? false : undefined}
                      style={
                        isParameterConfigField
                          ? { minWidth: 280, maxWidth: "100%" }
                          : undefined
                      }
                    >
                      {(selectValues || []).map((value: any, idx: number) => (
                        <Select.Option value={value.key} key={idx}>
                          {data.key === "parameter_configuration" ? value.label : t(value.label)}
                        </Select.Option>
                      ))}
                    </Select>
                    {isParameterConfigField && hasCommissioningTip(commissioningTip) && (
                      <Button
                        type="link"
                        icon={<QuestionCircleOutlined />}
                        onClick={() => setCommissioningTipOpen(true)}
                      >
                        {t("device_settings_commissioning_tip_button")}
                      </Button>
                    )}
                    {isValvePositionField && (
                      <div className="valve-telemetry-panel">
                        <span
                          className={`valve-telemetry-status${
                            valveTelemetryUpdatedAt == null
                              ? " valve-telemetry-status--unknown"
                              : valveTelemetryFresh
                                ? " valve-telemetry-status--fresh"
                                : " valve-telemetry-status--stale"
                          }`}
                        >
                          {telemetryDisplayRevision >= 0 &&
                            getValveTelemetryStatusLabel(valveTelemetryUpdatedAt)}
                        </span>
                        <Button
                          size="small"
                          icon={<ReloadOutlined />}
                          loading={valveTelemetryRefreshing}
                          onClick={() => refreshValveTelemetry(true)}
                        >
                          {t("settings:device_settings_valve_position_refresh_telemetry")}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          } else if (data.data_type === "boolean") {
            return (
              <div key={index} className="setting-block">
                <span>
                  {t(data.label)}
                  {data.tooltip && (
                    <Tooltip title={t(data.tooltip)}>
                      <InfoCircleOutlined
                        style={{
                          color: "#012124",
                          opacity: 0.4,
                          marginLeft: "5px",
                        }}
                      />
                    </Tooltip>
                  )}
                </span>
                <div className="input-container">
                  <Switch
                    value={formData[data.key] === "true"}
                    onChange={(checked) =>
                      setFormData({
                        ...formData,
                        [data.key]: checked ? "true" : "false",
                      })
                    }
                  />
                </div>
              </div>
            );
          }
        })}
        <div className="delete-container">
          <Button
            className="wifi-config-button"
            onClick={() => setConfigureWiFiOpen(true)}
          >
            {t("device_settings_configure_wifi_button", "Local Page Config")}
          </Button>
          <Popconfirm
            placement="topRight"
            title={t("device_settings_delete_device")}
            description={t("device_settings_home_description")}
            okButtonProps={{ loading: deleteLoading }}
            open={deleteOpen}
            onCancel={() => setDeleteOpen(false)}
            onConfirm={delete_device}
            icon={<QuestionCircleOutlined style={{ color: "red" }} />}
          >
            <Button 
              className="delete-button"
              onClick={() => setDeleteOpen(true)} 
              danger
            >
              {t("device_settings_delete_button")}
            </Button>
          </Popconfirm>
        </div>
      </div>
    </div>
  );
};

export default connect<StateProps, DispatchProps>(
  mapStateToProps,
  mapDispatchToProps
)(DeviceSettings);
